import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { ArrowUpRight, MailWarning, Clock3, Star, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtSigned, fmtPct, fmtPrice, fmtDateTime, pnlClass, LEDGER_KIND, SIDE, ccySymbol } from "@/lib/format";
import { StatCard, PageHeader, DataBadge, AssetIcon, Empty, DemoBanner, HelpTip } from "@/components/kit";
import { AlertButton } from "@/components/PriceAlerts";
import { LogoMark } from "@/components/Logo";
import { tileColor } from "@/components/MarketHeatmap";

const FavMap = ({ favs, onOpen }) => {
  const rows = favs.filter((a) => a.price != null).sort((a, b) => (b.change_pct || 0) - (a.change_pct || 0));
  if (!rows.length) return null;
  const max = Math.max(...rows.map((a) => Math.abs(a.change_pct || 0)), 0.5);
  return (
    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="fav-heatmap">
      {rows.map((a) => (
        <button key={a.symbol} onClick={() => onOpen(a.symbol)} title={`${a.name} · ${fmtPct(a.change_pct)}`} data-testid={`fav-heatmap-tile-${a.symbol}`}
          className="rounded-md px-2 py-2.5 text-center transition-transform hover:scale-105" style={{ background: tileColor(a.change_pct, max) }}>
          <div className="num truncate text-[11px] font-semibold">{a.symbol}</div>
          <div className="num text-xs">{fmtPct(a.change_pct)}</div>
        </button>
      ))}
    </div>
  );
};

const PERIODS = [["day", "Dia"], ["week", "Semana"], ["month", "Mês"], ["year", "Ano"]];

const AccountAlerts = ({ user }) => {
  const [link, setLink] = useState("");
  const resend = async () => {
    try { const { data } = await api.post("/auth/resend-verification"); setLink(data.dev_link); toast.success("Link de verificação gerado"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 mb-6">
      <DemoBanner text="Você está no MODO DEMONSTRAÇÃO: saldo virtual e operações simuladas. Nenhum dinheiro real é movimentado." />
      {user.status !== "active" && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm" data-testid="account-status-alert">
          <Clock3 className="h-4 w-4 text-amber-500" />Sua conta está em análise/pendente. A negociação será liberada após aprovação.
        </div>
      )}
      {!user.email_verified && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm" data-testid="email-verify-alert">
          <MailWarning className="h-4 w-4 text-sky-500" />Confirme seu e-mail para liberar saques.
          <Button size="sm" variant="outline" onClick={resend} data-testid="resend-verification-btn">Enviar link</Button>
          {link && <a href={link} className="break-all text-primary underline text-xs" data-testid="verification-dev-link">E-mail simulado: abrir link de verificação</a>}
        </div>
      )}
    </div>
  );
};

const WEEKDAY = (d) => d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");

const weekDays = (rows) => {
  const byDay = {};
  rows.forEach((r) => { byDay[new Date(r.ts).toDateString()] = r.equity; });
  const out = [];
  let last = rows.length ? rows[0].equity : null;
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const prev = last;
    if (byDay[d.toDateString()] != null) last = byDay[d.toDateString()];
    out.push({ label: WEEKDAY(d), date: d.toLocaleDateString("pt-BR"), equity: last, change: prev != null && last != null ? last - prev : 0 });
  }
  return out;
};

const WeekCard = () => {
  const [rows, setRows] = useState([]);
  usePoll(() => api.get("/me/equity-history?days=7").then((r) => setRows(r.data)).catch(() => {}), 60000);
  if (!rows.length) return null;
  const days = weekDays(rows);
  const first = rows[0].equity, lastV = rows[rows.length - 1].equity, diff = lastV - first;
  const pct = first ? (diff / first) * 100 : 0;
  const color = diff >= 0 ? "hsl(var(--up))" : "hsl(var(--down))";
  return (
    <div className="surface p-5 md:p-7 md:col-span-8" data-testid="week-equity-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="eyebrow">Sua semana · últimos 7 dias</div>
          <div className="num mt-2 text-2xl font-semibold" data-testid="week-equity-value">{fmtMoney(lastV)}</div></div>
        <div className={`num text-right text-sm font-semibold ${pnlClass(diff)}`} data-testid="week-equity-change">{fmtSigned(diff)}<div className="text-xs">{fmtPct(pct)} na semana</div></div>
      </div>
      <div className="mt-4 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={days} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <defs><linearGradient id="wk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <RTooltip formatter={(v) => fmtMoney(v)} labelFormatter={(l, p) => p?.[0]?.payload?.date || l} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Area type="monotone" dataKey="equity" stroke={color} strokeWidth={2} fill="url(#wk)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-1.5">{days.map((d) => (
        <div key={d.date} className="rounded-md bg-muted/40 px-1 py-2 text-center" data-testid={`week-day-${d.date}`}>
          <div className="text-[11px] capitalize text-muted-foreground">{d.label}</div>
          <div className={`num text-[11px] font-semibold ${pnlClass(d.change)}`}>{fmtSigned(d.change)}</div>
        </div>))}</div>
    </div>
  );
};

const parseGoal = (s) => {
  const t = s.trim().replace(/\s|US\$|€/g, "");
  if (t.includes(",") || /^\d{1,3}(\.\d{3})+$/.test(t)) return parseFloat(t.replace(/\./g, "").replace(",", "."));
  return parseFloat(t);
};

const GoalCard = ({ equity }) => {
  const { user, setUser } = useAuth();
  const goal = user.equity_goal || 0;
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const save = async (amount) => {
    try { const { data } = await api.put("/me/goal", { amount }); setUser({ ...user, ...data }); setEdit(false); toast.success(amount ? "Meta salva" : "Meta removida"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const pct = goal ? Math.min((equity / goal) * 100, 100) : 0;
  const left = Math.max(goal - equity, 0);
  return (
    <div className="surface p-5 md:p-7 md:col-span-4 flex flex-col" data-testid="goal-card">
      <div className="eyebrow flex items-center gap-1.5"><Target className="h-3.5 w-3.5 text-primary" />Meta de patrimônio</div>
      {edit || !goal ? (
        <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); const n = parseGoal(val); if (!(n > 0)) return toast.error("Informe um valor maior que zero"); save(n); }}>
          {!goal && <p className="text-sm text-muted-foreground">Defina quanto você quer alcançar e acompanhe aqui quanto falta.</p>}
          <div className="flex gap-2"><span className="grid place-items-center rounded-lg bg-muted px-3 num text-sm">{ccySymbol()}</span>
            <Input inputMode="decimal" placeholder="Ex.: 25.000" value={val} onChange={(e) => setVal(e.target.value)} className="h-11" data-testid="goal-input" /></div>
          <div className="flex gap-2"><Button className="h-11 flex-1" data-testid="goal-save-btn">Salvar meta</Button>
            {goal > 0 && <Button type="button" variant="ghost" className="h-11" onClick={() => setEdit(false)} data-testid="goal-cancel-btn">Cancelar</Button>}</div>
        </form>
      ) : (
        <div className="mt-4 flex flex-1 flex-col">
          <div className="num text-2xl font-semibold" data-testid="goal-value">{fmtMoney(goal)}</div>
          <div className="mt-4 h-3 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${pct}%` }} data-testid="goal-progress" /></div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground"><span className="num" data-testid="goal-pct">{pct.toFixed(1).replace(".", ",")}% alcançado</span><span className="num">{fmtMoney(equity)}</span></div>
          <div className="mt-4 text-sm" data-testid="goal-remaining">{left > 0 ? <>Faltam <span className="num font-semibold text-primary">{fmtMoney(left)}</span> para sua meta.</> : <span className="up font-semibold">Parabéns! Você alcançou sua meta.</span>}</div>
          <div className="mt-auto flex gap-2 pt-4"><Button size="sm" variant="outline" onClick={() => { setVal(String(goal).replace(".", ",")); setEdit(true); }} data-testid="goal-edit-btn">Alterar meta</Button>
            <Button size="sm" variant="ghost" onClick={() => save(null)} data-testid="goal-remove-btn">Remover</Button></div>
        </div>
      )}
    </div>
  );
};

const EquityChart = () => {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  usePoll(() => api.get(`/me/equity-history?days=${days}`).then((r) => setRows(r.data.map((x) => ({ ...x, label: new Date(x.ts).toLocaleDateString("pt-BR") })))), 60000, [days]);
  return (
    <div className="surface p-5 md:p-7 md:col-span-8" data-testid="equity-chart-card">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Desempenho da carteira</div>
        <div className="flex gap-1">{[[7, "7D"], [30, "30D"], [365, "1A"]].map(([d, l]) => (
          <Button key={d} size="sm" variant={days === d ? "secondary" : "ghost"} onClick={() => setDays(d)} data-testid={`equity-range-${l}`}>{l}</Button>
        ))}</div>
      </div>
      <div className="h-64 mt-4">
        {rows.length < 2 ? <Empty text="O gráfico será formado conforme o histórico da sua carteira for registrado (a cada 5 minutos)." /> : (
          <ResponsiveContainer>
            <AreaChart data={rows}>
              <defs><linearGradient id="eq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} minTickGap={40} />
              <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `${ccySymbol()}${(v / 1000).toFixed(1)}k`} />
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="equity" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#eq)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const Returns = ({ r }) => (
  <div className="surface p-5 md:p-7 md:col-span-4" data-testid="returns-card">
    <div className="eyebrow flex items-center gap-1.5">Rentabilidade<HelpTip text="Variação do patrimônio no período, descontando depósitos e saques. Exige histórico suficiente." /></div>
    <div className="mt-5 space-y-4">
      {PERIODS.map(([k, l]) => (
        <div key={k} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`return-${k}`}>
          <span className="text-sm text-muted-foreground">{l}</span>
          {r?.[k] ? <span className={`num font-semibold ${pnlClass(r[k].pnl)}`}>{fmtPct(r[k].pct)} <span className="text-xs">({fmtSigned(r[k].pnl)})</span></span>
            : <span className="text-xs text-muted-foreground">Dados insuficientes</span>}
        </div>
      ))}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  usePoll(() => api.get("/me/dashboard").then((r) => setD(r.data)).catch(() => {}), 5000);
  if (!d) return <div className="text-muted-foreground" data-testid="dashboard-loading">Carregando painel…</div>;
  const b = d.balances;
  return (
    <div data-testid="client-dashboard">
      <PageHeader eyebrow={`Conta ${user.client_code || ""}`} title={`Olá, ${user.name.split(" ")[0]}`}
        desc="Aqui está o resumo do seu dinheiro. Disponível é o que você pode usar agora; bloqueado está reservado para saques ou ordens pendentes."
        actions={<><Button className="h-11 rounded-xl" onClick={() => navigate("/mercados")} data-testid="go-markets-btn">Negociar</Button>
          <Button variant="outline" className="h-11 rounded-xl" onClick={() => navigate("/financeiro")} data-testid="go-wallet-btn">Depositar / Sacar</Button></>} />
      <AccountAlerts user={user} />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="relative overflow-hidden surface p-6 md:p-8 md:col-span-5 bg-gradient-to-br from-card to-card" data-testid="equity-card">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
          <LogoMark size={150} className="pointer-events-none absolute -bottom-8 -right-8 rotate-[-8deg] opacity-[0.08]" />
          <div className="eyebrow flex items-center gap-1.5">Patrimônio total (demo)<HelpTip text="Saldo em conta + resultado das posições abertas." /></div>
          <div className="num mt-4 text-4xl md:text-5xl font-semibold" data-testid="equity-value">{fmtMoney(b.equity)}</div>
          <div className={`num mt-2 text-sm ${pnlClass(b.unrealized_pnl)}`}>{fmtSigned(b.unrealized_pnl)} em posições abertas</div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-muted-foreground text-xs">Disponível</div><div className="num text-lg font-semibold up" data-testid="available-balance-display">{fmtMoney(b.available)}</div></div>
            <div><div className="text-muted-foreground text-xs">Bloqueado</div><div className="num text-lg font-semibold text-amber-500" data-testid="blocked-balance-display">{fmtMoney(b.blocked)}</div></div>
          </div>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 lg:grid-cols-3 gap-5">
          <StatCard testid="invested-card" label="Capital investido" value={fmtMoney(b.invested)} sub={`Exposição ${fmtMoney(b.exposure)}`} help="Margem alocada nas posições abertas." />
          <StatCard testid="deposits-card" label="Depósitos confirmados" value={fmtMoney(b.deposits_confirmed)} sub={d.deposits_pending ? `${fmtMoney(d.deposits_pending)} aguardando confirmação` : "Somente valores verificados"} />
          <StatCard testid="withdrawals-card" label="Saques" value={fmtMoney(d.withdrawals.completed)} sub={`Pendentes ${fmtMoney(d.withdrawals.pending)} · Solicitados ${fmtMoney(d.withdrawals.requested)}`} />
          <StatCard testid="realized-pnl-card" label="Lucro/prejuízo realizado" value={fmtSigned(b.realized_pnl)} tone={pnlClass(b.realized_pnl)} sub={`Comissões ${fmtMoney(b.total_commission)}`} />
          <StatCard testid="unrealized-pnl-card" label="Resultado não realizado" value={fmtSigned(b.unrealized_pnl)} tone={pnlClass(b.unrealized_pnl)} sub={`${b.open_positions} posições abertas`} />
          <StatCard testid="pending-orders-card" label="Ordens pendentes" value={b.pending_orders} sub={`Reservado ${fmtMoney(b.blocked_orders)}`} />
        </div>

        <EquityChart />
        <Returns r={d.returns} />
        <WeekCard />
        <GoalCard equity={b.equity} />

        <div className="surface p-5 md:p-7 md:col-span-7" data-testid="dashboard-positions">
          <div className="flex justify-between items-center"><div className="eyebrow">Posições abertas</div><Link to="/carteira" className="text-sm text-primary" data-testid="view-portfolio-link">Ver carteira</Link></div>
          {d.positions.length === 0 ? <Empty text="Você ainda não tem posições abertas." /> : (
            <div className="mt-4 divide-y divide-border">
              {d.positions.map((p) => (
                <button key={p.id} onClick={() => navigate(`/negociar/${p.symbol}`)} className="flex w-full items-center gap-3 py-3 text-left hover:bg-accent/40 rounded-lg px-2" data-testid={`dash-position-${p.id}`}>
                  <AssetIcon symbol={p.symbol} category={p.category} size={32} />
                  <div className="flex-1"><div className="font-semibold text-sm">{p.name}</div><div className="text-xs text-muted-foreground">{SIDE[p.side]} · {p.quantity}</div></div>
                  <div className={`num text-sm font-semibold ${pnlClass(p.unrealized_pnl)}`}>{fmtSigned(p.unrealized_pnl)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="surface p-5 md:p-7 md:col-span-5" data-testid="dashboard-favorites">
          <div className="eyebrow flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-primary" />Favoritos<HelpTip text="Toque no sino ao lado de um favorito para criar um alerta de preço." /></div>
          {d.favorites.length === 0 ? <Empty text="Adicione ativos aos favoritos na página Mercados." /> : (
            <>
            <FavMap favs={d.favorites} onOpen={(s) => navigate(`/negociar/${s}`)} />
            <div className="mt-4 divide-y divide-border">
              {d.favorites.map((a) => (
                <div key={a.symbol} className="flex items-center gap-1">
                <Link to={`/negociar/${a.symbol}`} className="flex flex-1 items-center gap-3 py-3 px-2 rounded-lg hover:bg-accent/40" data-testid={`dash-fav-${a.symbol}`}>
                  <AssetIcon symbol={a.symbol} category={a.category} size={32} />
                  <div className="flex-1"><div className="text-sm font-semibold">{a.name}</div><DataBadge type={a.data_type} testid={`dash-fav-badge-${a.symbol}`} /></div>
                  <div className="text-right"><div className="num text-sm">{fmtPrice(a.price, a.decimals)}</div><div className={`num text-xs ${pnlClass(a.change_pct)}`}>{fmtPct(a.change_pct)}</div></div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </Link>
                {a.price != null && <AlertButton q={a} />}
                </div>
              ))}
            </div>
            </>
          )}
        </div>

        <div className="surface p-5 md:p-7 md:col-span-12" data-testid="dashboard-ledger">
          <div className="eyebrow">Últimas movimentações</div>
          {d.recent_ledger.length === 0 ? <Empty text="Nenhuma movimentação." /> : (
            <div className="mt-4 divide-y divide-border">
              {d.recent_ledger.map((l) => (
                <div key={l.id} className="flex items-center justify-between py-3 text-sm">
                  <div><div className="font-medium">{LEDGER_KIND[l.kind] || l.kind}</div><div className="text-xs text-muted-foreground">{l.description} · {fmtDateTime(l.created_at)}</div></div>
                  <div className={`num font-semibold ${pnlClass(l.amount)}`}>{fmtSigned(l.amount, l.currency || "USD")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
